/**
 * CSP Nonce Management
 *
 * Este módulo gerencia o nonce CSP injetado pelo servidor.
 * O nonce deve ser gerado no servidor e inserido como meta tag ou script attribute.
 *
 * Fluxo esperado:
 * 1. Servidor gera um nonce único por requisição
 * 2. Insere em <meta id="csp-nonce" content="valor">
 * 3. Este código lê o nonce do DOM
 * 4. Aplica ao Emotion/MUI
 */

export function getCspNonce(): string | undefined {
  const normalizeNonce = (value: string | null | undefined): string | undefined => {
    if (!value) return undefined;

    const trimmed = value.trim();
    if (!trimmed || trimmed === 'NONCE_PLACEHOLDER' || trimmed === 'nonce-NONCE_PLACEHOLDER') {
      return undefined;
    }

    return trimmed.startsWith('nonce-') ? trimmed.slice(6) : trimmed;
  };

  // Procura por meta tag com id 'csp-nonce' (método mais comum)
  const nonceElement = document.querySelector('meta#csp-nonce');
  if (nonceElement) {
    const nonce = normalizeNonce(nonceElement.getAttribute('content'));
    if (nonce) return nonce;
  }

  // Compatibilidade com implementações que usam atributo name em vez de id
  const namedNonceElement = document.querySelector('meta[name="csp-nonce"]');
  if (namedNonceElement) {
    const nonce = normalizeNonce(namedNonceElement.getAttribute('content'));
    if (nonce) return nonce;
  }

  // Alternativa: procura por data attribute em script
  const scriptWithNonce = document.querySelector('script[nonce]');
  if (scriptWithNonce?.hasAttribute('nonce')) {
    return normalizeNonce(scriptWithNonce.getAttribute('nonce'));
  }

  return undefined;
}

/**
 * Valida se o nonce está disponível
 * @returns true se um nonce foi encontrado, false caso contrário
 */
export function hasValidNonce(): boolean {
  return !!getCspNonce();
}
